import { useEffect, useRef, useState } from "react";
import { FiArrowUp, FiCheckCircle, FiChevronDown, FiChevronLeft, FiChevronRight, FiChevronsLeft, FiChevronsRight, FiHome, FiMail, FiSearch, FiX } from "react-icons/fi";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getMarketingFarms } from "../../../../../api/adminMarketingFarms";
import filterIcon from "../../../../../assets/icons/admin-filter.svg";
import { ComponentLoader } from "../../../../../components/loaders";
import { ADMIN_PERMISSIONS, useAdminAuth } from "../../../../../providers/adminAuthentication.provider";
import { getAdminBroadcastSelection, getAdminDirectorySelection, saveAdminBroadcastSelection, saveAdminDirectorySelection } from "../../../../../utils/adminBroadcastSelection";
import { DashboardHeader } from "../../dashboards/dashboard-components";
import { buyerInitials } from "../../marketing-buyers/marketing-buyers.data";
import { SelectionConfirmation } from "../../marketing-buyers/selection-confirmation";

const farmSegments = [
  { value: "all", label: "All farms" },
  { value: "new_farms", label: "New farms" },
  { value: "active_farms", label: "Active farms" },
  { value: "inactive_farms", label: "Inactive farms" },
];

const emptySummary = { total_farms: 0, verified_farms_count: 0 };
const allowedLookbackDays = [7, 30, 60, 90, 360];
const allowedSortFields = ["name", "owner", "products", "rating", "joined"];
const positivePage = (value) => Math.max(1, Number.parseInt(value, 10) || 1);
const farmRecipient = (farm) => ({ id: farm.id, name: farm.name, detail: farm.ownerName });

export const FarmsDirectorySection = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { role } = useAdminAuth();
  const canMessage = ADMIN_PERMISSIONS.MARKETING.includes(role);
  const requestedSegment = searchParams.get("segment") || "all";
  const segment = farmSegments.some((item) => item.value === requestedSegment) ? requestedSegment : "all";
  const requestedDays = searchParams.get("days");
  const requestedNumericDays = Number(requestedDays);
  const days = requestedDays === "allTime" ? "allTime" : allowedLookbackDays.includes(requestedNumericDays) ? requestedNumericDays : 30;
  const page = positivePage(searchParams.get("page"));
  const query = searchParams.get("query") || "";
  const [debouncedQuery, setDebouncedQuery] = useState(query);
  const requestedSortBy = searchParams.get("sortBy") || "name";
  const sortBy = allowedSortFields.includes(requestedSortBy) ? requestedSortBy : "name";
  const sortOrder = searchParams.get("order") === "asc" ? "asc" : "desc";
  const [farms, setFarms] = useState([]);
  const pageSize = 10;
  const [summary, setSummary] = useState(emptySummary);
  const [pagination, setPagination] = useState({ total_items: 0, current_page: 1, total_pages: 1, pageSize });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [retryKey, setRetryKey] = useState(0);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const filtersRef = useRef(null);
  const [selected, setSelected] = useState(() => {
    const stored = getAdminDirectorySelection("farms");
    return new Set(stored.ids);
  });
  const [selectedRecords, setSelectedRecords] = useState(() => {
    const stored = getAdminDirectorySelection("farms");
    return new Map(stored.recipients.map((recipient) => [recipient.id, recipient]));
  });
  const [selectionPrompt, setSelectionPrompt] = useState(null);
  const activeSegment = farmSegments.find((item) => item.value === segment) || farmSegments[0];

  useEffect(() => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set("page", String(page));
      next.set("segment", segment);
      next.set("days", String(days));
      next.set("sortBy", sortBy);
      next.set("order", sortOrder);
      return next;
    }, { replace: true });
  }, [days, page, segment, setSearchParams, sortBy, sortOrder]);

  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedQuery(query), 350);
    return () => window.clearTimeout(timeout);
  }, [query]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError("");

    getMarketingFarms({ segment, days, page, pageSize, sortBy, order: sortOrder, search: debouncedQuery, forceRefresh: true })
      .then((response) => {
        if (!active) return;
        setFarms(response.data);
        setSummary(response.summary);
        setPagination(response.pagination);
      })
      .catch((error) => {
        if (!active) return;
        setFarms([]);
        setSummary(emptySummary);
        setLoadError(error?.response?.data?.message || error?.message || "Farms could not be loaded.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => { active = false; };
  }, [days, debouncedQuery, page, retryKey, segment, sortBy, sortOrder]);

  useEffect(() => {
    if (!filtersOpen) return undefined;

    const closeFilters = (event) => {
      if (event.key === "Escape" || (event.type === "pointerdown" && !filtersRef.current?.contains(event.target))) {
        setFiltersOpen(false);
      }
    };

    document.addEventListener("pointerdown", closeFilters);
    document.addEventListener("keydown", closeFilters);
    return () => {
      document.removeEventListener("pointerdown", closeFilters);
      document.removeEventListener("keydown", closeFilters);
    };
  }, [filtersOpen]);

  const visible = farms;

  const rangeStart = pagination.total_items ? ((pagination.current_page - 1) * pagination.pageSize) + 1 : 0;
  const rangeEnd = Math.min(pagination.current_page * pagination.pageSize, pagination.total_items);
  const selectableVisible = visible.filter((farm) => Boolean(farm.id));
  const allVisibleSelected = selectableVisible.length > 0 && selectableVisible.every((farm) => selected.has(farm.id));

  const toggleFarm = (farm) => {
    if (!farm.id) return;
    const nextSelected = new Set(selected);
    const nextRecords = new Map(selectedRecords);
    if (nextSelected.has(farm.id)) {
      nextSelected.delete(farm.id);
      nextRecords.delete(farm.id);
    } else {
      nextSelected.add(farm.id);
      nextRecords.set(farm.id, farmRecipient(farm));
    }
    setSelected(nextSelected);
    setSelectedRecords(nextRecords);
    saveAdminDirectorySelection("farms", [...nextSelected], [...nextRecords.values()]);
  };

  const toggleVisible = () => {
    const nextSelected = new Set(selected);
    const nextRecords = new Map(selectedRecords);
    if (allVisibleSelected) {
      selectableVisible.forEach((farm) => {
        nextSelected.delete(farm.id);
        nextRecords.delete(farm.id);
      });
    } else {
      selectableVisible.forEach((farm) => {
        nextSelected.add(farm.id);
        nextRecords.set(farm.id, farmRecipient(farm));
      });
    }
    setSelected(nextSelected);
    setSelectedRecords(nextRecords);
    saveAdminDirectorySelection("farms", [...nextSelected], [...nextRecords.values()]);
  };

  const updateDirectoryParams = (changes, replace = false) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      Object.entries(changes).forEach(([key, value]) => {
        if (value === undefined || value === null || value === "") next.delete(key);
        else next.set(key, String(value));
      });
      return next;
    }, { replace });
  };

  const changeFilter = (parameter) => (event) => updateDirectoryParams({ [parameter]: event.target.value, page: 1 });
  const changeSort = (value) => {
    const order = sortBy === value ? (sortOrder === "desc" ? "asc" : "desc") : "desc";
    updateDirectoryParams({ sortBy: value, order, page: 1 });
  };
  const SortableHeader = ({ label, value }) => {
    const active = sortBy === value;
    const direction = active ? sortOrder : "none";
    return <th aria-sort={active ? (sortOrder === "asc" ? "ascending" : "descending") : "none"}><button className={`table-sort ${active ? "active" : ""}`} type="button" onClick={() => changeSort(value)} aria-label={`Sort by ${label}, ${active && sortOrder === "desc" ? "ascending" : "descending"}`}><span>{label}</span><FiArrowUp className={`sort-direction ${direction}`} aria-hidden="true" /></button></th>;
  };

  const continueToBroadcast = () => {
    const selection = saveAdminBroadcastSelection("farms", [...selected], [...selectedRecords.values()]);
    setSelectionPrompt(null);
    navigate("/admin/internal/marketing/broadcast", { state: { broadcast: { ...selection, target: "custom_selection" } } });
  };

  const openBroadcast = () => {
    if (!selected.size) {
      navigate("/admin/internal/marketing/broadcast", { state: { broadcast: { audience: "farms", target: "segment", segment, days } } });
      return;
    }

    const stored = getAdminBroadcastSelection();
    const newIds = [...selected].filter((id) => stored.audience !== "farms" || !stored.ids.includes(id));
    if (stored.ids.length && stored.audience === "farms" && newIds.length) {
      setSelectionPrompt({ title: "Add to custom selection?", description: `You already have ${stored.ids.length.toLocaleString()} farm${stored.ids.length === 1 ? "" : "s"} selected. Add ${newIds.length.toLocaleString()} more?`, confirmLabel: "Add and continue" });
      return;
    }
    if (stored.ids.length && stored.audience !== "farms") {
      setSelectionPrompt({ title: "Replace custom selection?", description: `Your saved selection contains ${stored.ids.length.toLocaleString()} recipients from another directory. A broadcast can target only one audience type at a time.`, confirmLabel: "Replace and continue" });
      return;
    }
    continueToBroadcast();
  };

  return (
    <>
      <DashboardHeader title="Farms" description={canMessage ? "Find farms by activity, select farm owners, and prepare targeted communication." : "Review farm ownership, activity, products, verification and ratings."} showActions={false} />

      <section className="audience-summary-grid farm-summary" aria-label="Farm directory summary">
        <article><span className="summary-icon"><FiHome aria-hidden="true" /></span><div><span>Total farms</span><strong>{summary.total_farms.toLocaleString()}</strong><small>Matching the current filters</small></div></article>
        <article><span className="summary-icon"><FiCheckCircle aria-hidden="true" /></span><div><span>Verified farms</span><strong>{summary.verified_farms_count.toLocaleString()}</strong><small>Verified in this audience</small></div></article>
      </section>

      <section className="buyers-card">
        <header className="buyers-toolbar">
          <div><h2>{activeSegment.label}</h2><p>{selected.size ? `${selected.size.toLocaleString()} farm${selected.size === 1 ? "" : "s"} selected across pages.` : `${days === "allTime" ? "All-time" : `${days}-day`} farm activity window`}</p></div>
          <div className="buyer-actions">
            <div className="filter-menu" ref={filtersRef}>
              <button className={`filter-trigger ${filtersOpen ? "open" : ""}`} type="button" onClick={() => setFiltersOpen((current) => !current)} aria-expanded={filtersOpen} aria-controls="farm-directory-filters">
                <img src={filterIcon} alt="" />
                <span>Filters</span>
                <FiChevronDown aria-hidden="true" />
              </button>
              {filtersOpen && <div className="filter-popup" id="farm-directory-filters">
                <header><div><strong>Filter farms</strong><span>Refine the directory results</span></div><button type="button" onClick={() => setFiltersOpen(false)} aria-label="Close filters"><FiX /></button></header>
                <div className="filter-fields">
                  <label className="audience-filter"><span>Segment</span><select value={segment} onChange={changeFilter("segment")} aria-label="Filter farms by segment">{farmSegments.map((item) => <option value={item.value} key={item.value}>{item.label}</option>)}</select></label>
                  <label className="audience-filter compact-filter"><span>Lookback</span><select value={days} onChange={changeFilter("days")} aria-label="Farm lookback period"><option value={7}>7 days</option><option value={30}>30 days</option><option value={60}>60 days</option><option value={90}>90 days</option><option value={360}>360 days</option><option value="allTime">All time</option></select></label>
                </div>
                <button className="filter-done" type="button" onClick={() => setFiltersOpen(false)}>Done</button>
              </div>}
            </div>
            <label className="buyer-search"><FiSearch aria-hidden="true" /><input value={query} onChange={(event) => updateDirectoryParams({ query: event.target.value, page: 1 }, true)} placeholder="Search farms" aria-label="Search farms" /></label>
            {canMessage && pagination.total_items > 0 && <button className="message-button" type="button" onClick={openBroadcast}><FiMail aria-hidden="true" />{selected.size ? `Broadcast to selected (${selected.size})` : `Broadcast to ${activeSegment.label}`}</button>}
          </div>
        </header>

        {loading ? <div className="directory-loader"><ComponentLoader /></div> : <div className="buyers-table-wrap">
          <table className="buyers-table">
            <thead><tr>{canMessage && <th className="select-cell"><input type="checkbox" checked={allVisibleSelected} onChange={toggleVisible} aria-label="Select all farms shown on this page" /></th>}<SortableHeader label="Farm" value="name" /><SortableHeader label="Owner" value="owner" /><th>Phone</th><th>Location</th><SortableHeader label="Products" value="products" /><SortableHeader label="Rating" value="rating" /><th>Verified</th><th>Status</th><SortableHeader label="Created" value="joined" /></tr></thead>
            <tbody>{loadError ? <tr className="empty-directory-row"><td colSpan={canMessage ? 10 : 9}><FiHome aria-hidden="true" /><strong>Farms could not be loaded</strong><span>{loadError}</span><button type="button" className="retry-button" onClick={() => setRetryKey((current) => current + 1)}>Try again</button></td></tr> : visible.length ? visible.map((farm, index) => <tr key={farm.id || `${farm.slug}-${index}`} className={farm.id && selected.has(farm.id) ? "selected" : ""}>{canMessage && <td className="select-cell" data-label="Select"><input type="checkbox" checked={Boolean(farm.id && selected.has(farm.id))} disabled={!farm.id} onChange={() => toggleFarm(farm)} aria-label={farm.id ? `Select ${farm.name}` : `${farm.name} cannot be selected because an ID was not provided`} /></td>}<td className="buyer-cell" data-label="Farm"><div className="buyer-person farm-person"><span className="buyer-avatar" aria-hidden="true">{buyerInitials(farm.name)}</span><span><strong>{farm.name}</strong><small>{farm.slug}</small></span></div></td><td data-label="Owner"><div className="owner-contact"><strong>{farm.ownerName}</strong>{farm.ownerEmail !== "Not available" ? <a href={`mailto:${farm.ownerEmail}`}>{farm.ownerEmail}</a> : <span>Not available</span>}</div></td><td data-label="Phone">{farm.ownerPhone}</td><td data-label="Location">{farm.location}</td><td data-label="Products"><strong className="order-count">{farm.products}</strong></td><td data-label="Rating"><span className="rating-value">{farm.rating ? farm.rating.toFixed(1) : "—"} <small>({farm.reviews})</small></span></td><td data-label="Verified"><span className={`farm-state ${farm.verified ? "positive" : "neutral"}`}>{farm.verified ? "Verified" : "Unverified"}</span></td><td data-label="Status"><span className={`farm-state ${farm.active ? "positive" : "neutral"}`}>{farm.active ? "Active" : "Inactive"}</span></td><td data-label="Created">{farm.createdDate}</td></tr>) : <tr className="empty-directory-row"><td colSpan={canMessage ? 10 : 9}><FiHome aria-hidden="true" /><strong>No farms match this view</strong><span>Try changing the segment, lookback window, or search term.</span></td></tr>}</tbody>
          </table>
        </div>}

        <footer className="buyers-pagination"><span>{pagination.total_items ? `Showing ${rangeStart}–${rangeEnd} of ${pagination.total_items.toLocaleString()} farms` : "No farm records available"}</span><div><button type="button" disabled={loading || pagination.current_page <= 1} onClick={() => updateDirectoryParams({ page: 1 })} aria-label="Go to first page" title="First page"><FiChevronsLeft /></button><button type="button" disabled={loading || pagination.current_page <= 1} onClick={() => updateDirectoryParams({ page: pagination.current_page - 1 })} aria-label="Previous page" title="Previous page"><FiChevronLeft /></button><span>Page {pagination.current_page} of {pagination.total_pages}</span><button type="button" disabled={loading || pagination.current_page >= pagination.total_pages} onClick={() => updateDirectoryParams({ page: pagination.current_page + 1 })} aria-label="Next page" title="Next page"><FiChevronRight /></button><button type="button" disabled={loading || pagination.current_page >= pagination.total_pages} onClick={() => updateDirectoryParams({ page: pagination.total_pages })} aria-label="Go to last page" title="Last page"><FiChevronsRight /></button></div></footer>
      </section>
      <SelectionConfirmation prompt={selectionPrompt} onCancel={() => setSelectionPrompt(null)} onConfirm={continueToBroadcast} />
    </>
  );
};
